import { type ReactElement, type RefObject, useRef, useState } from "react";
import { useBooleanState } from "~/common/hooks/useBooleanState";
import { useOnMount } from "~/common/hooks/useOnMount";
import { Collapse } from "~/common/mui";
import ToggleButton from "~/common/components/Links/ToggleButton";

import classes from "~/common/components/Table/TanStackTable.module.less";

interface Props {
  children: (ref: RefObject<HTMLDivElement>) => ReactElement;
  maxItemLimit: number;
  items: unknown[];
}

export const PartiallyCollapsedTableWrapper = ({ children, items, maxItemLimit }: Props) => {
  const { val: showAll, toggle: toggleShowAll } = useBooleanState(false);

  const materialTableWrapperRef = useRef<HTMLDivElement>(null);
  const [materialTableSize, setMaterialTableSize] = useState(0);

  const showMoreEnabled = items.length > maxItemLimit;

  useOnMount(() => {
    const id = window.setTimeout(() => {
      if (materialTableWrapperRef.current && showMoreEnabled) {
        const wrapper = materialTableWrapperRef.current;

        const headerHeight = wrapper.querySelector("thead")?.getBoundingClientRect().height ?? 0;
        const titleHeight = wrapper.querySelector("." + classes.title)?.getBoundingClientRect().height ?? 0;
        const rows = Array.prototype.slice.call(wrapper.querySelectorAll("tbody > tr"), 0, maxItemLimit);
        const tableHeight = rows.reduce((sum, b) => sum + b.getBoundingClientRect().height, 0);
        // The top margin of the header needs to be excluded from this, as the collapse element starts exactly with the header
        setMaterialTableSize(headerHeight + tableHeight + titleHeight);
      }
    }, 150);
    return () => clearTimeout(id);
  });

  const table = children(materialTableWrapperRef);

  if (!showMoreEnabled) {
    return table;
  }
  return (
    <>
      <Collapse in={showAll} collapsedSize={materialTableSize}>
        {table}
      </Collapse>
      {showMoreEnabled && <ToggleButton className={"mt-2"} isOpen={showAll} onToggle={toggleShowAll} />}
    </>
  );
};
